import {
  IMessage,
  IVisibleTo,
  MESSAGE_CONTENT_TYPE,
  MESSAGE_TYPE,
} from "@home-health-notify/domain";
import {
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import { useContext, useState } from "react";
import { IMessageTypeBubble } from "../MessagePanel";
import {
  AcknowledgeFooter,
  AcknowledgeHeader,
  MoreAcknowledgeLabel,
  MoreAcknowledgeWrapper,
  ThumbsUpAltOutlined,
  ThumbsUpIcon,
} from "./AcknowledgeBubble.style";

export const AcknowledgeBubble = (props: IMessageTypeBubble) => {
  return (
    <>
      {props.isSender && <AcknowledgeHeaderComponent message={props.message} />}
      {props.children}
      <AcknowledgeFooterComponent
        message={props.message}
        disableActions={props.disableActions}
      />
    </>
  );
};

const AcknowledgeHeaderComponent = ({ message }: { message: IMessage }) => {
  let messageData = message.getMessage();
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const isAcknowledged = messageData.type === MESSAGE_TYPE.ACKNOWLEDGED;
  return (
    messageData.contentType !== MESSAGE_CONTENT_TYPE.REMINDER && (
      <AcknowledgeHeader isAcknowledged={isAcknowledged} variant="body2">
        <ThumbsUpIcon color="inherit" fontSize="small" />
        {isAcknowledged
          ? tNs("message.acknowledged")
          : tNs("message.acknowledge")}
      </AcknowledgeHeader>
    )
  );
};

const AcknowledgeFooterComponent = ({
  message,
  disableActions,
}: {
  message: IMessage;
  disableActions?: boolean;
}) => {
  const { t } = useTranslation();
  let messageData = message.getMessage();
  const tNs = (key: string) => t(key, { ns: "message" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { userDirectory } = useContext(UserDirectoryContext);
  const currentOrganization = selectedOrgUser?.getOrganization();
  const visibleTo: IVisibleTo[] = (messageData.visibleTo ?? []).filter(
    (visibleTo: IVisibleTo) => visibleTo.acknowledgementRequired === 1,
  );
  const [expanded, setExpanded] = useState<boolean>(false);

  const remainingCount = visibleTo.length - 5;
  const visibleToDisplay = expanded ? visibleTo : visibleTo.slice(0, 5);

  const isSelf = (id: string) => {
    let userInfo = userDirectory.getUserInfo(id);
    return currentOrganization
      ? userInfo.id === currentOrganization.userProfile.id
      : false;
  };

  const getUserTitle = (id: string, hasUserAcknowledged: boolean) => {
    let userInfo = userDirectory.getUserInfo(id);
    if (
      currentOrganization &&
      userInfo.id === currentOrganization.userProfile.id
    ) {
      return hasUserAcknowledged
        ? tNs("message.acknowledgedSelf")
        : tNs("message.acknowledgeSelf");
    }
    return `${userInfo.title}`;
  };

  const onAcknowledgeClick = () => {
    message.acknowledge();
  };

  const onShowMore = () => {
    setExpanded(true);
  };

  return (
    <>
      {visibleToDisplay.map((visibleTo: IVisibleTo) => {
        let hasUserAcknowledged: boolean = Boolean(
          visibleTo.isAcknowledged,
        ).valueOf();
        let isSelfUser = isSelf(visibleTo.id);
        return (
          <AcknowledgeFooter
            variant="body2"
            key={visibleTo.id}
            hasUserAcknowledged={hasUserAcknowledged}
            isSelf={isSelfUser}
            disableActions={disableActions}
            onClick={e => {
              e.stopPropagation();
              !disableActions &&
                isSelfUser &&
                !hasUserAcknowledged &&
                onAcknowledgeClick();
            }}
          >
            {!hasUserAcknowledged ? (
              <ThumbsUpAltOutlined fontSize="small" />
            ) : (
              <ThumbsUpIcon fontSize="small" />
            )}
            {getUserTitle(visibleTo.id, hasUserAcknowledged)}
          </AcknowledgeFooter>
        );
      })}
      <MoreAcknowledge
        remainingCount={remainingCount}
        tNs={tNs}
        onShowMore={onShowMore}
        expanded={expanded}
      />
    </>
  );
};

// Removed duplicate MoreAcknowledge definition

interface MoreAcknowledgeProps {
  remainingCount: number;
  tNs: (key: string) => string;
  onShowMore: () => void;
  expanded: boolean;
}

const MoreAcknowledge: React.FC<MoreAcknowledgeProps> = ({
  remainingCount,
  tNs,
  onShowMore,
  expanded,
}) => {
  return (
    remainingCount >= 1 &&
    !expanded && (
      <MoreAcknowledgeWrapper onClick={onShowMore}>
        <MoreAcknowledgeLabel>{`+${remainingCount} ${tNs("message.moreAcknowledge")}`}</MoreAcknowledgeLabel>
      </MoreAcknowledgeWrapper>
    )
  );
};

export default AcknowledgeBubble;
